import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { Link } from 'react-router-dom';

export default function About() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    website_alt: '',
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotValue = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim() || '';

    if (honeypotValue) {
      setFormStatus('success');
      setFormError('');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const body = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
      body.delete('website_alt');
      const response = await fetch('https://readdy.ai/api/form/d9oti4osenbniolesll0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      if (response.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '', website_alt: '' });
      } else {
        const serverMsg =
          (parsed && ((parsed as Record<string, unknown>).meta as Record<string, unknown>)?.message as string) ||
          (parsed && (parsed as Record<string, unknown>).message as string) ||
          responseText ||
          '提交失败，请稍后重试';
        setFormStatus('error');
        setFormError(String(serverMsg));
      }
    } catch (err: unknown) {
      setFormStatus('error');
      setFormError('网络错误，请检查您的连接后重试');
    }
  };

  const advantages = [
    {
      icon: 'ri-shield-check-line',
      title: '海量资源',
      description: '收录超过10,000部韩国漫画，涵盖校园、恋爱、奇幻、热血等全品类题材，满足不同读者的阅读需求。',
    },
    {
      icon: 'ri-flashlight-line',
      title: '每日更新',
      description: '与韩国漫画平台同步更新，每日稳定更新最新章节，让您第一时间追更心仪的作品。',
    },
    {
      icon: 'ri-hd-line',
      title: '高清画质',
      description: '所有漫画均提供高清画质，自动适配手机和电脑屏幕，带来沉浸式的阅读体验。',
    },
    {
      icon: 'ri-eye-off-line',
      title: '无广告干扰',
      description: '纯净阅读环境，无弹窗广告打扰，让您沉浸在漫画世界中，享受纯粹的阅读乐趣。',
    },
    {
      icon: 'ri-user-star-line',
      title: '无需注册',
      description: '无需注册账号即可直接阅读，降低使用门槛，打开网站就能畅享海量韩漫资源。',
    },
    {
      icon: 'ri-global-line',
      title: '多端适配',
      description: 'PC端和移动端完美适配，无论您在哪里，都能随时随地享受阅读韩漫的乐趣。',
    },
  ];

  const faqs = [
    {
      q: '韩国漫画网是否需要注册才能阅读？',
      a: '不需要！韩国漫画网完全免费开放，无需注册任何账号，打开网站即可直接阅读所有漫画作品，没有任何隐藏门槛。',
    },
    {
      q: '漫画资源多久更新一次？',
      a: '我们每日都会更新最新热门漫画作品，与韩国漫画平台保持同步。通常每天上午10点会集中更新一批新章节，您可以在"最新更新"栏目中查看。',
    },
    {
      q: '是否支持手机阅读？',
      a: '当然支持！韩国漫画网采用响应式设计，完美适配手机、平板和电脑等各种设备。您可以在任何设备上获得流畅的阅读体验。',
    },
    {
      q: '为什么完全免费？如何盈利？',
      a: '韩国漫画网致力于为韩漫爱好者提供优质的阅读体验。我们通过品牌合作和广告联盟获得运营资金，确保用户无需付费即可享受服务。',
    },
    {
      q: '如何找到我想看的漫画？',
      a: '您可以通过顶部的搜索框输入漫画名称进行搜索，也可以按分类浏览不同题材的漫画。我们还提供排行榜和热门推荐，帮助您发现优质作品。',
    },
  ];

  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.a,
        },
      })),
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen bg-background-50">
        {/* Breadcrumb */}
        <div className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm py-4" aria-label="面包屑导航">
              <Link to="/" className="text-foreground-500 hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
              <span className="text-foreground-800 font-medium">关于我们</span>
            </nav>
          </div>
        </div>

        {/* Hero */}
        <section className="relative w-full h-[320px] md:h-[400px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20library%20illustration%2C%20beautiful%20cozy%20reading%20space%20with%20bookshelves%20filled%20with%20colorful%20comic%20books%2C%20warm%20natural%20lighting%20from%20large%20windows%2C%20comfortable%20reading%20nook%2C%20soft%20cream%20and%20wood%20tones%2C%20Korean%20webtoon%20art%20style%2C%20peaceful%20atmosphere%2C%20inviting%20and%20warm%20composition&width=1600&height=900&seq=about-hero-comic&orientation=landscape"
            alt="关于韩国漫画网"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-background-50"></div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-3">关于我们</h1>
            <p className="text-white/80 text-base md:text-lg max-w-xl text-center">
              致力于为韩漫爱好者提供最优质的在线阅读体验
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-14 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                品牌介绍
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                韩国漫画网成立于2024年，是国内领先的韩国漫画在线阅读平台。我们致力于汇集海量优质韩漫资源，
                为广大漫画爱好者提供一个免费、纯净、高品质的阅读空间。从校园青春到奇幻冒险，从甜蜜恋爱到热血战斗，
                韩国漫画网涵盖了几乎所有题材的韩漫作品，让每一位读者都能在这里找到自己喜爱的漫画。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mt-4">
                我们与多家韩国漫画创作团队建立了深度合作关系，确保每日同步更新最新章节。
                同时，我们拥有一支专业的汉化团队，所有作品均由经验丰富的译者进行精准翻译，
                力求将原作的精彩完美呈现给中国读者。在韩国漫画网，阅读从未如此简单和享受。
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="bg-background-100 py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {[
                { value: '10,000+', label: '漫画作品' },
                { value: '500万+', label: '月活用户' },
                { value: '99.7%', label: '好评率' },
                { value: '365天', label: '每日更新' },
              ].map((stat) => (
                <div key={stat.label}>
                  <div className="text-3xl md:text-4xl font-heading font-bold text-primary-500 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Advantages */}
        <section className="py-14 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                为什么选择我们
              </h2>
              <p className="text-sm text-foreground-500">六大核心优势，打造极致阅读体验</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {advantages.map((adv) => (
                <div
                  key={adv.title}
                  className="p-6 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-200 transition-colors"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 mb-4">
                    <i className={`${adv.icon} text-xl text-accent-600`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">{adv.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{adv.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 md:py-16 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500">关于韩国漫画网的常见疑问解答</p>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <details key={index} className="group bg-background-50 rounded-lg border border-background-200/70">
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <h3 className="text-sm font-medium text-foreground-800 pr-4">{faq.q}</h3>
                    <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                    <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-14 md:py-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500">
                如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="text-center p-6 bg-background-50 rounded-lg border border-background-200/70">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-50 mx-auto mb-3">
                  <i className="ri-mail-line text-xl text-primary-500"></i>
                </div>
                <h4 className="font-medium text-sm text-foreground-800 mb-1">邮箱</h4>
                <p className="text-sm text-foreground-500">contact@koreancomic.cn</p>
              </div>
              <div className="text-center p-6 bg-background-50 rounded-lg border border-background-200/70">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 mx-auto mb-3">
                  <i className="ri-map-pin-line text-xl text-accent-600"></i>
                </div>
                <h4 className="font-medium text-sm text-foreground-800 mb-1">地址</h4>
                <p className="text-sm text-foreground-500">北京市朝阳区漫画大厦12层</p>
              </div>
              <div className="text-center p-6 bg-background-50 rounded-lg border border-background-200/70">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-secondary-100 mx-auto mb-3">
                  <i className="ri-time-line text-xl text-secondary-600"></i>
                </div>
                <h4 className="font-medium text-sm text-foreground-800 mb-1">工作时间</h4>
                <p className="text-sm text-foreground-500">周一至周五 9:00-18:00</p>
              </div>
            </div>

            <form
              data-readdy-form
              id="contact-form"
              onSubmit={handleSubmit}
              className="bg-background-100/50 rounded-lg border border-background-200/70 p-6 md:p-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                  placeholder="请输入主题"
                />
              </div>
              <div className="mb-4">
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
              </div>
              {/* Honeypot */}
              <div className="anti-spam-field">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>
              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full sm:w-auto px-8 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 rounded-full font-medium text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'submitting' ? '提交中...' : '发送留言'}
              </button>
              {formStatus === 'success' && (
                <p className="mt-3 text-sm text-green-600">感谢您的留言！我们会尽快与您取得联系。</p>
              )}
              {formStatus === 'error' && formError && (
                <p className="mt-3 text-sm text-primary-600">{formError}</p>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}